/**
 * Earning Schedule Redux Slice
 * Manages state for scheduled earning type changes
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import earningScheduleService from '../../services/AdminApis/earningScheduleService';

// ==================== Async Thunks ====================

export const fetchScheduledChanges = createAsyncThunk(
  'earningSchedule/fetchScheduledChanges',
  async (_, { rejectWithValue }) => {
    try {
      return await earningScheduleService.getScheduledChanges();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const scheduleEarningTypeChange = createAsyncThunk(
  'earningSchedule/scheduleChange',
  async (scheduleData, { rejectWithValue }) => {
    try {
      return await earningScheduleService.scheduleChange(scheduleData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const modifySchedule = createAsyncThunk(
  'earningSchedule/modifySchedule',
  async (modifyData, { rejectWithValue }) => {
    try {
      return await earningScheduleService.modifySchedule(modifyData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelSchedule = createAsyncThunk(
  'earningSchedule/cancelSchedule',
  async (cancelData, { rejectWithValue }) => {
    try {
      return await earningScheduleService.cancelSchedule(cancelData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSetupStatus = createAsyncThunk(
  'earningSchedule/fetchSetupStatus',
  async (sellerId, { rejectWithValue }) => {
    try {
      return await earningScheduleService.getSetupStatus(sellerId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const bulkScheduleChanges = createAsyncThunk(
  'earningSchedule/bulkSchedule',
  async (bulkData, { rejectWithValue }) => {
    try {
      return await earningScheduleService.bulkSchedule(bulkData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const bulkCancelSchedules = createAsyncThunk(
  'earningSchedule/bulkCancel',
  async (bulkData, { rejectWithValue }) => {
    try {
      return await earningScheduleService.bulkCancel(bulkData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Slice Definition ====================

const earningScheduleSlice = createSlice({
  name: 'earningSchedule',
  initialState: {
    scheduledChanges: [],
    setupStatuses: {},
    loading: false,
    error: null,
    selectedSellers: [],
    mode: 'inline', // 'inline' | 'batch'
    filters: {
      status: 'all', // 'all' | 'pending' | 'applying_soon' | 'completed'
      sellerId: null,
      search: ''
    }
  },
  reducers: {
    setMode: (state, action) => {
      state.mode = action.payload;
    },
    setFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    clearFilters: (state) => {
      state.filters = {
        status: 'all',
        sellerId: null,
        search: ''
      };
    },
    selectSeller: (state, action) => {
      const sellerId = action.payload;
      if (!state.selectedSellers.includes(sellerId)) {
        state.selectedSellers.push(sellerId);
      }
    },
    deselectSeller: (state, action) => {
      const sellerId = action.payload;
      state.selectedSellers = state.selectedSellers.filter(id => id !== sellerId);
    },
    clearSelection: (state) => {
      state.selectedSellers = [];
    },
    selectAllSellers: (state, action) => {
      state.selectedSellers = action.payload;
    },
    updateSetupProgress: (state, action) => {
      const { sellerId, progress } = action.payload;
      state.setupStatuses[sellerId] = {
        ...state.setupStatuses[sellerId],
        ...progress
      };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch Scheduled Changes
    builder
      .addCase(fetchScheduledChanges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchScheduledChanges.fulfilled, (state, action) => {
        state.loading = false;
        // Handle different API response structures
        const changes = action.payload?.scheduled_changes || 
                       action.payload?.data?.scheduled_changes ||
                       (Array.isArray(action.payload) ? action.payload : []) ||
                       [];
        state.scheduledChanges = changes;
      })
      .addCase(fetchScheduledChanges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Schedule Change
    builder
      .addCase(scheduleEarningTypeChange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(scheduleEarningTypeChange.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.success) {
          state.scheduledChanges.push({
            seller_id: action.payload.sellerId,
            old_value: action.payload.oldType,
            new_value: action.payload.newType,
            effective_date: action.payload.effectiveDate,
            status: 'scheduled'
          });
        }
      })
      .addCase(scheduleEarningTypeChange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Modify Schedule
    builder
      .addCase(modifySchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(modifySchedule.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.success) {
          const index = state.scheduledChanges.findIndex(
            change => change.seller_id === action.meta.arg.sellerId
          );
          if (index !== -1) {
            state.scheduledChanges[index].effective_date = action.payload.newEffectiveDate;
          }
        }
      })
      .addCase(modifySchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Cancel Schedule
    builder
      .addCase(cancelSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSchedule.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.success) {
          state.scheduledChanges = state.scheduledChanges.filter(
            change => change.seller_id !== action.meta.arg.sellerId
          );
        }
      })
      .addCase(cancelSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch Setup Status
    builder
      .addCase(fetchSetupStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSetupStatus.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.seller_id) {
          state.setupStatuses[action.payload.seller_id] = action.payload;
        }
      })
      .addCase(fetchSetupStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Bulk Schedule
    builder
      .addCase(bulkScheduleChanges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkScheduleChanges.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.success) {
          action.payload.success.forEach(result => {
            state.scheduledChanges.push({
              seller_id: result.sellerId,
              old_value: result.oldType,
              new_value: result.newType,
              effective_date: result.effectiveDate,
              status: 'scheduled'
            });
          });
        }
        state.selectedSellers = [];
      })
      .addCase(bulkScheduleChanges.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Bulk Cancel
    builder
      .addCase(bulkCancelSchedules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkCancelSchedules.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.success) {
          action.payload.success.forEach(result => {
            state.scheduledChanges = state.scheduledChanges.filter(
              change => change.seller_id !== result.sellerId
            );
          });
        }
        state.selectedSellers = [];
      })
      .addCase(bulkCancelSchedules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// ==================== Actions ====================

export const {
  setMode,
  setFilter,
  clearFilters,
  selectSeller,
  deselectSeller,
  clearSelection,
  selectAllSellers,
  updateSetupProgress,
  clearError
} = earningScheduleSlice.actions;

// ==================== Selectors ====================

export const selectScheduledChanges = (state) => state.loyalty.earningSchedule?.scheduledChanges || [];
export const selectSetupStatus = (sellerId) => (state) => state.loyalty.earningSchedule?.setupStatuses[sellerId] || null;
export const selectEarningScheduleLoading = (state) => state.loyalty.earningSchedule?.loading || false;
export const selectEarningScheduleError = (state) => state.loyalty.earningSchedule?.error || null;
export const selectSelectedSellers = (state) => state.loyalty.earningSchedule?.selectedSellers || [];
export const selectMode = (state) => state.loyalty.earningSchedule?.mode || 'inline';
export const selectFilters = (state) => state.loyalty.earningSchedule?.filters || { status: 'all', sellerId: null, search: '' };

export const selectPendingChangesCount = (state) => {
  const changes = state.loyalty.earningSchedule?.scheduledChanges || [];
  return changes.filter(change => change.status === 'scheduled').length;
};

export default earningScheduleSlice.reducer;
